<template>
    <div class="payment-info grid-x align-justify" id="cart-payment-info">
        <div class="cell shrink">
            <p class="display-large color-dark-gray cart-title semi-bold">Payment</p>
        </div>
        <div class="cell">
            <div class="grid-x" v-if="isMembershipActive">
                <div class="cell payment-option">
                    <checkbox @change="selectedPaymentMethod = 'membership'" :radio="true" label-class="payment-option-label" label="Membership Card (5% discount)" :checked="selectedPaymentMethod === 'membership'"></checkbox>
                    <div v-if="selectedPaymentMethod === 'membership'" class="payment-info">
                        <text-input
                            label="Membership number"
                            name="membershipNumber"
                            :value="membershipNumber"
                            @change="membershipNumber = $event"
                            @blur="validateMembership"
                            :error-message="errorMessage('membershipNumber')"
                            :required="true"
                        ></text-input>
                        <button class="button expanded" @click="applyMembership" :disabled="isMembershipApplied">Use this payment method</button>
                        <p class="color-alert display-small" v-if="!isMembershipApplied">{{ errorMessage('membership') }}</p>
                    </div>
                </div>
                <div class="cell payment-option">
                    <div class="grid-x justify-between items-center">
                        <div class="cell auto credit-card-opt">
                            <checkbox label-class="payment-option-label" @change="selectedPaymentMethod = 'credit'" :radio="true" label="Credit Card" :checked="selectedPaymentMethod === 'credit'"></checkbox>
                        </div>
                        <div class="cell shrink">
                            <div class="credit-icon">
                                <span v-html="Icon.CREDIT_CARD"></span>
                            </div>
                        </div>
                    </div>
                    <div v-if="selectedPaymentMethod === 'credit'" class="payment-info">
                        <credit-card-input
                            label="Card number"
                            name="ccNumber"
                            :type="ccType"
                            :value="ccNumber"
                            @change="ccNumber = $event"
                            @blur="validateField('ccNumber')"
                            :error-message="errorMessage('ccNumber')"
                            :required="true"
                        ></credit-card-input>
                        <div class="grid-x grid-padding-x">
                            <div class="cell auto">
                                <expiration-date
                                    label="Exp date"
                                    :value="ccExpDate"
                                    @change="ccExpDate = $event"
                                    @blur="validateField('ccExpDate')"
                                    :error-message="errorMessage('ccExpDate')"
                                    :is-required="true"
                                >
                                </expiration-date>
                            </div>
                            <div class="cell auto">
                                <text-input
                                    label="CVV"
                                    type="number"
                                    name="cvv"
                                    autocomplete="cc-csc"
                                    :maxLength="4"
                                    :value="cvv"
                                    @change="cvv = $event"
                                    @blur="validateField('cvv')"
                                    :error-message="errorMessage('cvv')"
                                    :required="true"
                                ></text-input>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="cell" v-else>
                <credit-card-input
                    label="Card number"
                    name="ccNumber"
                    :type="ccType"
                    :value="ccNumber"
                    @change="ccNumber = $event"
                    @blur="validateField('ccNumber')"
                    :error-message="errorMessage('ccNumber')"
                    :required="true"
                ></credit-card-input>
                <div class="grid-x grid-padding-x">
                    <div class="cell auto">
                        <expiration-date
                            label="Exp date"
                            :value="ccExpDate"
                            @change="ccExpDate = $event"
                            @blur="validateField('ccExpDate')"
                            :error-message="errorMessage('ccExpDate')"
                            :is-required="true"
                        >
                        </expiration-date>
                    </div>
                    <div class="cell auto">
                        <text-input
                            label="CVV"
                            type="number"
                            name="cvv"
                            autocomplete="cc-csc"
                            :maxLength="4"
                            :value="cvv"
                            @change="cvv = $event"
                            @blur="validateField('cvv')"
                            :error-message="errorMessage('cvv')"
                            :required="true"
                        ></text-input>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>
<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { getModule } from 'vuex-module-decorators';
import UserModule from '@/stores/modules/user';
import CartModule from '@/stores/modules/cart';
import TextInput from '@/components/common/forms/TextInput.vue';
import ExpirationDate from './ExpirationDate.vue';
import CreditCardInput from './CreditCardInput.vue';
import Settings, { SettingNamespace } from '@/utils/settings';
import Checkbox from '@/components/common/ui/Checkbox.vue';
import { Icon } from '@/front/icons';
@Component({
    components: { TextInput, ExpirationDate, CreditCardInput, Checkbox }
})
export default class CartPaymentInfo extends Vue
{
    userModule!: UserModule;
    cartModule!: CartModule;
    isMembershipActive: boolean = false;

    paymentMethods: {label: string, value: string}[] = [
        {
            label: 'Credit Card',
            value: 'credit'
        }
    ];
    selectedPaymentMethod: string = 'credit';

    async created() {
        this.userModule = getModule(UserModule, this.$store);
        this.cartModule = getModule(CartModule, this.$store);
        const settings = await Settings.getInstance();
        const isMembershipAllowed = settings.get(SettingNamespace.PAYMENT, 'accept_loyalty_cards')?.value

        if (isMembershipAllowed) {
            this.paymentMethods.push({
                label: 'Membership Card (5% discount)',
                value: 'membership'
            });
            this.isMembershipActive = true;
        }

        this.$watch(() => this.selectedPaymentMethod, (value) => {
            if (value === 'credit') {
                this.cartModule.applyMembership(false);
                this.cartModule.clearCheckoutError('membershipNumber');
            }
        });
    }

    openLoginModal() {
        this.userModule.openLoginModal();
    }

    validateField(field: string) {
        this.cartModule.validateCheckoutField(field);
    }

    validateMembership() {
        this.cartModule.validateCheckoutField('membershipNumber');
        if (this.cartModule.checkoutErrors.find((error) => error.source === 'membershipNumber')) {
            return;
        }
    }

    async applyMembership() {
        const isMembershipValid = await this.userModule.validateMembership(this.membershipNumber);
        if (isMembershipValid) {
            this.cartModule.clearCheckoutError('ccNumber');
            this.cartModule.clearCheckoutError('ccExpDate');
            this.cartModule.clearCheckoutError('cvv');
            this.cartModule.applyMembership(true);
        }

        if (!isMembershipValid) {
            this.cartModule.addCheckoutError({ source: 'membership', message: "Unable to apply membership. Please verify your membership card number or contact support for assistance." });
        }
    }

    get Icon() {
        return Icon;
    }

    set ccNumber(value: string) {
        this.cartModule.clearCheckoutError('ccNumber');
        this.cartModule.setCCNumber(value);
    }

    get isMembershipApplied() {
        return this.cartModule.isMembershipApplied;
    }

    get ccNumber() {
        return this.cartModule.checkoutFields.ccNumber.value;
    }

    get membershipNumber() {
        return this.cartModule.checkoutFields.membershipNumber.value;
    }

    set membershipNumber(value: string) {
        this.cartModule.applyMembership(false);
        this.cartModule.clearCheckoutError('membershipNumber');
        this.cartModule.clearCheckoutError('membership');
        this.cartModule.setMembershipNumber(value);
    }

    set ccExpDate(value: string) {
        this.cartModule.clearCheckoutError('ccExpDate');
        this.cartModule.setCCExpDate(value);
    }

    get ccExpDate() {
       return this.cartModule.checkoutFields.ccExpDate.value;
    }

    set cvv(value: string) {
        this.cartModule.clearCheckoutError('cvv');
        this.cartModule.setCVV(value);
    }

    get isValidMembershipNumber() {
        return this.cartModule.checkoutFields.membershipNumber.value.length > 0 && this.cartModule.checkoutErrors.find((error) => error.source === 'membershipNumber') === undefined;
    }

    get hideCardNumber() {
        return (cardNumber: string) => {
            return cardNumber.replace(/\d(?=\d{4})/g, '*');
        };
    }

    get cvv() {
        return this.cartModule.checkoutFields.cvv.value;
    }

    get ccType() {
        return this.cartModule.ccType;
    }

    get errorMessage() {
        return (field: string) => {
            const error = this.cartModule.checkoutErrors.find(error => error.source === field);
            if (error) {
                return error.message;
            }
        }
    }
}
</script>
<style lang="scss">
.payment-option {
    padding-top: 10px;
    padding-bottom: 10px;

    &:first-child {
        border-bottom: 1px solid var(--light-gray);
    }

    .payment-option-label {
        margin-left: 15px;
    }

    .credit-card-opt {
        align-self: center;
    }

    .credit-icon {
        border: 1px solid var(--light-gray);
        border-radius: 999px;
        padding: 3px 10px;

        svg {
            position: relative;
            top: 5px;
        }
    }

    .payment-info {
        padding-top: 15px;
    }
}
</style>
